import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { clientDisplayName } from '../../utils/clientName';
import EventEquipmentDraft from './EventEquipmentDraft';

function initialForm(event, defaultDate, initialData) {
  const source = event || initialData || {};
  return {
    name:            source.name            || '',
    event_date:      source.event_date?.slice(0, 10) || defaultDate || '',
    days:            1,
    setup_start:     source.setup_start     || '',
    start_time:      source.start_time      || '',
    end_time:        source.end_time        || '',
    place:           source.place           || '',
    client:          source.client          || '',
    sales_referent:  source.sales_referent  || '',
    odoo_code:       source.odoo_code       || '',
    notes:           source.notes           || '',
    status:          source.status          || 'confirmado',
  };
}

export default function EventDrawer({ event, defaultDate, initialData, onClose, onSaved }) {
  const isEdit = !!event;

  const [form, setForm] = useState(() => initialForm(event, defaultDate, initialData));
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [searchQ, setSearchQ]             = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lugares, setLugares]             = useState([]);
  const [clients, setClients]             = useState([]);
  const [personnel, setPersonnel]         = useState([]);
  const [equipmentDraft, setEquipmentDraft] = useState(() => initialData?.equipment_parsed || []);

  function set(k) { return e => setForm(f => ({ ...f, [k]: e.target.value })); }

  // El formulario se alimenta con un directorio de Agenda. No depende de que
  // el usuario también tenga acceso a los módulos maestros de Ventas/RRHH.
  useEffect(() => {
    api.get('/events/editor-directory')
      .then(({ data }) => {
        setLugares(Array.isArray(data?.lugares) ? data.lugares : []);
        setClients(Array.isArray(data?.clients) ? data.clients : []);
        setPersonnel(Array.isArray(data?.personnel) ? data.personnel : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get(`/events/search?q=${encodeURIComponent(searchQ)}&by=staff`);
        setSearchResults(data);
      } catch (_) { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  function importFrom(src) {
    setForm(f => ({
      ...f,
      name:           src.name           || f.name,
      place:          src.place          || f.place,
      client:         src.client         || f.client,
      sales_referent: src.sales_referent || f.sales_referent,
      odoo_code:      src.odoo_code      || f.odoo_code,
    }));
    setSearchQ('');
    setSearchResults([]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const unresolvedEquipment = equipmentDraft.flatMap(block => block?.items || []).find(item =>
      typeof item === 'string' || item?.resource_type === 'legacy'
    );
    if (!isEdit && unresolvedEquipment) {
      const name = typeof unresolvedEquipment === 'string'
        ? unresolvedEquipment
        : unresolvedEquipment.resource_name;
      setError(`Vinculá "${name}" con un artículo, rack o pack del inventario antes de crear el evento.`);
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { days: _d, ...payload } = form;
        await api.put(`/events/${event.id}`, payload);
      } else {
        await api.post('/events', { ...form, equipment_parsed: equipmentDraft });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal event-drawer-modal${isEdit ? '' : ' event-drawer-modal-create'}`}>
        <h2 className="modal-title">{isEdit ? 'Editar evento' : 'Nuevo evento'}</h2>

        <form onSubmit={handleSubmit}>
          <div className={isEdit ? '' : 'event-drawer-create-layout'}>
          <div className="event-drawer-form-fields">
          {!isEdit && (
            <div className="form-group">
              <label className="form-label">Importar datos de evento existente</label>
              <input
                className="input"
                placeholder="Buscar por lugar o referente comercial…"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
              />
              {searchLoading && <div className="text-mute text-sm" style={{ marginTop: 4 }}>Buscando…</div>}
              {searchResults.length > 0 && (
                <div className="import-dropdown">
                  {searchResults.map(r => (
                    <div key={r.id} className="import-option" onClick={() => importFrom(r)}>
                      <div className="fw-700">{r.name}</div>
                      <div className="text-mute text-sm">{[r.client, r.place].filter(Boolean).join(' · ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Nombre *</label>
            <input className="input" required value={form.name} onChange={set('name')} />
          </div>

          <div className="agenda-form-row">
            <div className="form-group">
              <label className="form-label">Fecha *</label>
              <input className="input" type="date" required value={form.event_date} onChange={set('event_date')} />
            </div>
            {!isEdit && (
              <div className="form-group" style={{ flex: '0 0 90px' }}>
                <label className="form-label">Días</label>
                <input
                  className="input"
                  type="number" min="1" max="7"
                  value={form.days}
                  onChange={e => setForm(f => ({ ...f, days: parseInt(e.target.value) || 1 }))}
                />
              </div>
            )}
          </div>

          <div className="agenda-form-row">
            <div className="form-group">
              <label className="form-label">Hora armado</label>
              <input className="input" placeholder="08:30" value={form.setup_start} onChange={set('setup_start')} />
            </div>
            <div className="form-group">
              <label className="form-label">Inicio</label>
              <input className="input" placeholder="18:00" value={form.start_time} onChange={set('start_time')} />
            </div>
            <div className="form-group">
              <label className="form-label">Fin</label>
              <input className="input" placeholder="22:00" value={form.end_time} onChange={set('end_time')} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Estado</label>
            <select className="input" value={form.status} onChange={set('status')}>
              <option value="confirmado">Confirmado</option>
              <option value="posible">Posible</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Lugar</label>
            <select className="input" value={form.place} onChange={set('place')}>
              <option value="">Sin lugar</option>
              {/* Si el evento trae un lugar viejo que no está en la lista, se conserva como opción */}
              {form.place && !lugares.some(l => l.name === form.place || (l.espacios || []).some(e => `${l.name} - ${e.name}` === form.place)) && (
                <option value={form.place}>{form.place}</option>
              )}
              {lugares.flatMap(l => [
                <option key={`l-${l.id}`} value={l.name}>{l.name}</option>,
                ...(l.espacios || []).map(e => (
                  <option key={`e-${e.id}`} value={`${l.name} - ${e.name}`}>{l.name} - {e.name}</option>
                )),
              ])}
            </select>
          </div>

          <div className="agenda-form-row">
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select className="input" value={form.client} onChange={set('client')}>
                <option value="">Sin cliente</option>
                {form.client && !clients.some(c => clientDisplayName(c) === form.client) && (
                  <option value={form.client}>{form.client}</option>
                )}
                {clients.map(c => <option key={c.id} value={clientDisplayName(c)}>{clientDisplayName(c)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Referente comercial</label>
              <select className="input" value={form.sales_referent} onChange={set('sales_referent')}>
                <option value="">Sin referente</option>
                {/* Valor viejo fuera de la lista: se conserva como opción */}
                {form.sales_referent && !personnel.some(p => p.first_name === form.sales_referent) && (
                  <option value={form.sales_referent}>{form.sales_referent}</option>
                )}
                {personnel.map(p => <option key={p.id} value={p.nickname || p.first_name}>{p.nickname || [p.first_name, p.last_name].filter(Boolean).join(' ')}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">N° Orden (Odoo)</label>
            <input className="input" placeholder="S02249" value={form.odoo_code} onChange={set('odoo_code')} />
          </div>

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="input" rows="3" value={form.notes} onChange={set('notes')} style={{ resize: 'vertical' }} />
          </div>
          </div>

          {!isEdit && <EventEquipmentDraft
            value={equipmentDraft}
            onChange={setEquipmentDraft}
            importedFromPdf={Boolean(initialData?.equipment_parsed?.length)}
          />}
          </div>

          {error && <div className="text-red text-sm" style={{ marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
